import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../components/Header";
import SmartBasketBanner from "../components/SmartBasketBanner";
import HomeQuickActionsBar from "../components/HomeQuickActionsBar";
import DailyPlanCard from "../components/DailyPlanCard";
import HomeQuickCompareSection from "../components/HomeQuickCompareSection";
import Footer from "../components/Footer";
import { useAppSelector } from "../store/hooks";
import { apiRequest } from "../api/client";

function parsePrice(value) {
  if (value == null) return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  const normalized = String(value).trim().replace(",", ".").replace(/[^0-9.-]/g, "");
  if (!normalized) return null;
  const parsed = Number.parseFloat(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

function calcSavingsFromComparison(product) {
  const comp = Array.isArray(product?.comparison) ? product.comparison : [];
  if (!comp.length) return null;
  const prices = comp
    .map((item) => parsePrice(item?.price ?? item?.unit_price ?? item?.price_per_unit ?? item?.offer_price))
    .filter((p) => typeof p === "number" && Number.isFinite(p));
  if (!prices.length) return null;
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  const saving = max - min;
  return saving > 0 && Number.isFinite(saving) ? saving : null;
}

function FemtyzHome({ onOpenAuth, onOpenMenu }) {
  const navigate = useNavigate();
  const authToken = useAppSelector((s) => s.auth.token);
  const isLoggedIn = Boolean(authToken);
  const features = useAppSelector((s) => s.actowiz.features);
  const [bestOverallBasketState, setBestOverallBasketState] = useState({
    loading: false,
    error: "",
    groceryListId: null,
    savingMessage: "",
    bestOverallBasket: "",
    bannerMode: "new_account", // new_account | list_created | added_list | common_products
    showBestOverallBasketFallback: true,
  });
  const [listPickerOpen, setListPickerOpen] = useState(false);
  const [listPickerOptions, setListPickerOptions] = useState([]);
  const [listPickerResolver, setListPickerResolver] = useState(null);

  const closeListPicker = useCallback(() => {
    listPickerResolver?.(null);
    setListPickerOpen(false);
    setListPickerOptions([]);
    setListPickerResolver(null);
  }, [listPickerResolver]);

  const chooseList = useCallback((lists) => {
    return new Promise((resolve) => {
      setListPickerOptions(lists);
      setListPickerResolver(() => resolve);
      setListPickerOpen(true);
    });
  }, []);

  useEffect(() => {
    if (!listPickerOpen) return undefined;
    const onEsc = (e) => {
      if (e.key === "Escape") closeListPicker();
    };
    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, [listPickerOpen, closeListPicker]);

  useEffect(() => {
    if (!isLoggedIn) {
      setBestOverallBasketState({
        loading: false,
        error: "",
        groceryListId: null,
        savingMessage: "",
        bestOverallBasket: "",
      });
      return;
    }

    let cancelled = false;
    (async () => {
      setBestOverallBasketState((prev) => ({ ...prev, loading: true, error: "" }));
      const res = await apiRequest("api/users/best_overall_basket", { method: "GET" });
      if (cancelled) return;

      if (!res.ok) {
        setBestOverallBasketState((prev) => ({
          ...prev,
          loading: false,
          error: res.data?.message || "Failed to load best overall basket.",
        }));
        return;
      }

      const message = res.data?.message || "";
      const topSavingMessage = res.data?.saving_message || "";

      // Case 4: common products found (backend provides these fields on grocery_lists)
      const groceryLists = Array.isArray(res.data?.grocery_lists) ? res.data.grocery_lists : [];
      const firstList = groceryLists[0] || null;
      const firstSavingMessage = firstList?.saving_message || "";
      const firstBestOverallBasket = firstList?.best_overall_basket || "";
      if (firstSavingMessage || firstBestOverallBasket) {
        const groceryListId = firstList?.grocery_list_id ?? firstList?.id ?? null;
        setBestOverallBasketState({
          loading: false,
          error: "",
          groceryListId,
          savingMessage: firstSavingMessage,
          bestOverallBasket: firstBestOverallBasket,
          bannerMode: "common_products",
          showBestOverallBasketFallback: true,
        });
        return;
      }

      // Case 1/2/3: no common products / or no lists yet (power_basket_list)
      const power = res.data?.power_basket_list || null;
      const groceryListId = power?.grocery_list_id ?? power?.id ?? null;
      const powerSavingMessage = power?.saving_message || "";

      // Case 2: list exists but no products added yet (backend sends top-level saving_message)
      if (!power && topSavingMessage) {
        setBestOverallBasketState({
          loading: false,
          error: "",
          groceryListId: res.data?.grocery_list_id ?? res.data?.id ?? null,
          savingMessage: topSavingMessage,
          bestOverallBasket: "",
          bannerMode: "list_created",
          showBestOverallBasketFallback: false,
        });
        return;
      }

      const totalBasketAmount = parsePrice(power?.total_basket_amount ?? null);
      const lowestBasketText = typeof power?.lowest_basket === "string" ? power.lowest_basket.trim() : "";
      const backendBestOverallBasket =
        typeof power?.best_overall_basket === "string" ? power.best_overall_basket.trim() : "";

      const storeAmounts = power?.total_store_amounts && typeof power.total_store_amounts === "object"
        ? power.total_store_amounts
        : null;

      let secondLowestText = "";
      if (storeAmounts) {
        const entries = Object.entries(storeAmounts)
          .map(([store, amount]) => ({
            store: String(store || "").trim(),
            amount: parsePrice(amount),
          }))
          .filter((x) => x.store && typeof x.amount === "number");

        entries.sort((a, b) => a.amount - b.amount);
        if (entries.length >= 2) {
          const second = entries[1];
          secondLowestText = `2nd lowest basket: ${second.store} (£${second.amount.toFixed(2)})`;
        }
      }

      const bestOverallBasketParts = [];
      // Prefer backend-provided text when available.
      if (backendBestOverallBasket) {
        bestOverallBasketParts.push(backendBestOverallBasket);
      } else {
        if (secondLowestText) bestOverallBasketParts.push(secondLowestText);
        else if (lowestBasketText) bestOverallBasketParts.push(lowestBasketText);
        if (typeof totalBasketAmount === "number") {
          bestOverallBasketParts.push(`Total basket: £${totalBasketAmount.toFixed(2)}`);
        }
      }

      const bannerMode = groceryListId
        ? storeAmounts
          ? "added_list"
          : "list_created"
        : "new_account";

      // Case 1: new account (no list) → do not show basket summary line.
      const bestOverallBasketText =
        bannerMode === "new_account" ? "" : bestOverallBasketParts.join(" • ");

      setBestOverallBasketState({
        loading: false,
        error: "",
        groceryListId,
        savingMessage: powerSavingMessage || topSavingMessage || message,
        bestOverallBasket: bestOverallBasketText,
        bannerMode,
        showBestOverallBasketFallback: bannerMode !== "new_account",
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [isLoggedIn]);

  const handleAddToList = useCallback(async (product) => {
    const minimalRes = await apiRequest("api/grocery_lists_minimal", { method: "GET" });
    if (!minimalRes.ok) {
      const errors = Array.isArray(minimalRes.data?.errors) ? minimalRes.data.errors : null;
      throw new Error(
        errors?.join("\n") || minimalRes.data?.message || "Failed to load grocery lists."
      );
    }

    const lists = Array.isArray(minimalRes.data)
      ? minimalRes.data
      : Array.isArray(minimalRes.data?.data)
        ? minimalRes.data.data
        : [];

    if (!lists.length) {
      throw new Error("No grocery list found. Please create a list first.");
    }

    let selectedListId = null;
    if (lists.length === 1) {
      selectedListId =
        lists[0]?.id ?? lists[0]?.grocery_list_id ?? lists[0]?.list_id ?? null;
    } else {
      const selected = await chooseList(lists);
      if (!selected) {
        const cancelError = new Error("ADD_TO_LIST_CANCELLED");
        cancelError.code = "ADD_TO_LIST_CANCELLED";
        throw cancelError;
      }
      selectedListId =
        selected?.id ?? selected?.grocery_list_id ?? selected?.list_id ?? null;
    }
    if (!selectedListId) {
      throw new Error("Unable to select grocery list.");
    }

    const productId = product?.product_id ?? product?.pid ?? product?.sku ?? null;
    if (!productId) {
      throw new Error("Invalid product.");
    }

    const addRes = await apiRequest(`api/grocery_lists/${selectedListId}/grocery_products`, {
      method: "POST",
      body: {
        product_id: String(productId),
        product_name: product?.title || product?.product_name || product?.name || product?.sku || "",
        category: product?.category || product?.category_name || undefined,
        subcategory: product?.subcategory || product?.sub_category || product?.subCategory || undefined,
        platform:
          String(product?.platform || "")
            .trim()
            .toLowerCase()
            .replace("morrisons", "morrison")
            .replace("sainsburys", "sainsbury") || undefined,
        image_url: product?.images?.[0] || "",
        price: product?.price ?? null,
        weight: product?.packsize || product?.weight || "",
        quantity: 1,
      },
    });

    if (!addRes.ok) {
      const errors = Array.isArray(addRes.data?.errors) ? addRes.data.errors : null;
      throw new Error(errors?.join("\n") || addRes.data?.message || "Failed to add product to list.");
    }
  }, [chooseList]);

  const offerTabLowestSaving = (() => {
    const latest12 = (Array.isArray(features) ? features : []).slice(0, 12);
    const savings = latest12
      .map((p) => calcSavingsFromComparison(p))
      .filter((n) => typeof n === "number" && Number.isFinite(n));
    return savings.length ? Math.min(...savings) : null;
  })();

  return (
    <div className="min-h-screen bg-[#F5F7FB] text-[#24303A]">
      <Header onOpenAuth={onOpenAuth} onOpenMenu={onOpenMenu} />

      <main className="mx-auto flex max-w-8xl gap-6 px-4 py-6 md:px-6">

        <section className="flex-1 space-y-8">
          <SmartBasketBanner
            isLoggedIn={isLoggedIn}
            saveUpToAmount={offerTabLowestSaving ?? 6.4}
            savingMessage={bestOverallBasketState.savingMessage}
            bestOverallBasket={bestOverallBasketState.bestOverallBasket}
            showBestOverallBasketFallback={bestOverallBasketState.showBestOverallBasketFallback}
            reviewLabel={bestOverallBasketState.bannerMode === "new_account" ? "Create Grocery" : "Review Basket"}
            onReviewBasket={() => {
              if (bestOverallBasketState.bannerMode === "new_account") {
                navigate("/settings?tab=grocery&view=create");
                return;
              }
              const id = bestOverallBasketState.groceryListId;
              if (id) {
                navigate(
                  `/settings?tab=grocery&view=detail&grocery_list_id=${encodeURIComponent(String(id))}`
                );
              } else {
                navigate("/settings?tab=grocery");
              }
            }}
            onComparePrices={
              bestOverallBasketState.bannerMode === "new_account" ? null : () => navigate("/features")
            }
          />


          {/* Hero / dashboard summary card */}
          <div className="rounded-3xl border border-[#E6EAF0] bg-white px-5 py-8 shadow-sm sm:px-5 sm:py-8">
            <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
              <div className="max-w-xl">
                <p className="text-[14px] font-normal uppercase tracking-wide text-[#6E7B86]">
                  FEMTYZ Dashboard
                </p>
                <h1 className="mt-2 text-[28px] font-semibold leading-tight text-[#24303A]">
                  Find the cheapest groceries faster
                </h1>
                <p className="mt-2 text-[14px] font-normal text-[#6E7B86]">
                  Compare prices across retailers, then add the best deals to your grocery list.
                </p>

                {/* <div className="mt-4 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => navigate("/top-deals")}
                    className="rounded-full bg-[#7352C7] px-4 py-2 text-[14px] font-extrabold text-white shadow-sm hover:bg-[#6546c3]"
                  >
                    View Top Deals
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate("/features")}
                    className="rounded-full border border-[#E6EAF0] bg-white px-4 py-2 text-[14px] font-extrabold text-[#7352C7] shadow-sm hover:bg-[#F5F7FB]"
                  >
                    Browse Featured
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate("/search")}
                    className="rounded-full border border-[#E6EAF0] bg-white px-4 py-2 text-[14px] font-extrabold text-[#7352C7] shadow-sm hover:bg-[#F5F7FB]"
                  >
                    Search
                  </button>
                </div> */}
              </div>
              <DailyPlanCard />
            </div>
          </div>
          <HomeQuickActionsBar />
          <HomeQuickCompareSection onAddToList={handleAddToList} />
          {/* <SupermarketTabs /> */}
        </section>
      </main>

      <Footer />

      {listPickerOpen && (
        <div className="fixed inset-0 z-[80]">
          <button
            type="button"
            className="absolute inset-0 bg-black/40"
            aria-label="Close list selector"
            onClick={closeListPicker}
          />
          <div className="relative mx-auto flex min-h-screen max-w-3xl items-center justify-center px-4 py-8">
            <div className="w-full max-w-md rounded-2xl border border-[#E6EAF0] bg-white p-5 shadow-xl">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="text-base font-bold text-[#24303A]">Choose Grocery List</h3>
                  <p className="mt-1 text-[14px] text-slate-500">Select the list where you want to add this product.</p>
                </div>
                <button
                  type="button"
                  onClick={closeListPicker}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-[#E6EAF0] bg-white text-slate-600 hover:bg-slate-50"
                  aria-label="Close popup"
                  title="Close"
                >
                  ✕
                </button>
              </div>
              <div className="mt-4 space-y-2">
                {listPickerOptions.map((list, index) => {
                  const listId = list?.id ?? list?.grocery_list_id ?? list?.list_id ?? "";
                  const listTitle = list?.title || `List ${index + 1}`;
                  return (
                    <button
                      key={`${listId}-${index}`}
                      type="button"
                      className="w-full rounded-xl border border-[#E6EAF0] bg-white px-3 py-2 text-left hover:bg-slate-50"
                      onClick={() => {
                        listPickerResolver?.(list);
                        setListPickerOpen(false);
                        setListPickerOptions([]);
                        setListPickerResolver(null);
                      }}
                    >
                      <p className="text-[14px] font-semibold text-slate-800">{listTitle}</p>
                      <p className="text-[11px] text-slate-500">ID: {String(listId || "-")}</p>
                    </button>
                  );
                })}
              </div>
              <div className="mt-4 flex justify-end">
                <button
                  type="button"
                  onClick={closeListPicker}
                  className="rounded-lg border border-[#E6EAF0] bg-white px-3 py-1.5 text-[14px] font-semibold text-slate-700 hover:bg-slate-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default FemtyzHome;